import {
  Scene,
  Vector3,
  AbstractMesh,
  Bone,
  Skeleton,
  Color3,
  AnimationGroup,
  SceneLoader,
  PointLight,
  SpotLight,
} from '@babylonjs/core';
import { logger } from '../core/Logger';
import { UnifiedLightManager, type LightHandle } from './UnifiedLightManager';
import { getBoneWorldPosition } from './BoneAttachmentSystem';
import { createProjectileMesh } from './MeshFactory';
import type { AttachedMeshInstance, AttachedMeshSpec, BoneLightOptions, ProjectileSpec } from '../types/combat';
import type { Character } from './Character';
import type { ExtractionResult } from '../assets/EntityAssetExtractor';
import type { CharacterAsset } from '../assets/CharacterLoader';

/**
 * Manages visual effects for combat: channel lights, static FX, and attached meshes.
 * Extracted from CombatSystem to reduce its size and improve single-responsibility.
 * 
 * IMPORTANT: Uses UnifiedLightManager for all dynamic lights to avoid shader recompilation.
 */
export class CombatEffectsController {
  private scene: Scene;
  private channelLights: Map<string, PointLight | SpotLight> = new Map();
  private channelLightHandles: Map<string, LightHandle> = new Map();
  private nextLightId = 0;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  // ============================================================
  // Channel Lights
  // ============================================================

  /**
   * Create a point light attached to a character bone.
   * Returns a light ID that can be used to update or destroy the light.
   */
  createBoneLight(
    attacker: Character,
    options: BoneLightOptions,
    activeChannelLights: string[]
  ): string {
    const lightId = `channel_light_${this.nextLightId++}`;

    // Get bone position
    const position = getBoneWorldPosition(attacker, options.bone) ?? attacker.character.getAbsolutePosition();

    // Calculate world offset if specified
    let finalPosition = position.clone();
    if (options.offset) {
      const forward = attacker.character.getDirection(Vector3.Forward());
      const right = Vector3.Cross(Vector3.Up(), forward).normalize();
      const up = Vector3.Up();

      // Transform local offset to world space
      const worldOffset = forward
        .scale(options.offset.z)
        .add(right.scale(options.offset.x))
        .add(up.scale(options.offset.y));
      finalPosition.addInPlace(worldOffset);
    }

    // Acquire combat light from UnifiedLightManager (high priority, preempts map lights)
    const handle = UnifiedLightManager.acquireCombatLight({
      position: finalPosition,
      color: options.color ?? new Color3(1, 1, 1),
      intensity: options.intensity ?? 1.0,
      range: options.range ?? 5.0
    });
    
    if (!handle) {
      logger.combat.warn(`[CombatEffectsController] UnifiedLightManager failed to acquire light`);
      return lightId;
    }
    
    // Track both handle and light reference
    this.channelLightHandles.set(lightId, handle);
    if (handle.light) {
      this.channelLights.set(lightId, handle.light);
    }
    activeChannelLights.push(lightId);

    logger.combat.debug(`[CombatEffectsController] Acquired pooled light: ${lightId}`);
    return lightId;
  }

  /**
   * Update the position of a bone light to follow a bone.
   */
  updateBoneLightPosition(attacker: Character, lightId: string, boneName: string, offset?: Vector3): void {
    const light = this.channelLights.get(lightId);
    if (!light) return;

    // Get bone position
    const bonePos = getBoneWorldPosition(attacker, boneName);
    if (!bonePos) return;

    const finalPosition = bonePos.clone();

    // Apply offset if specified
    if (offset) {
      const forward = attacker.character.getDirection(Vector3.Forward());
      const right = Vector3.Cross(Vector3.Up(), forward).normalize();
      const up = Vector3.Up();

      const worldOffset = forward.scale(offset.z).add(right.scale(offset.x)).add(up.scale(offset.y));
      finalPosition.addInPlace(worldOffset);
    }

    light.position.copyFrom(finalPosition);
  }

  /**
   * Update the color and intensity of a bone light.
   */
  updateBoneLightColor(lightId: string, color: Color3, intensity?: number): void {
    const light = this.channelLights.get(lightId);
    if (!light) return;

    light.diffuse = color;
    if (intensity !== undefined) light.intensity = intensity;
  }

  /**
   * Destroy a bone light by its ID.
   * Releases the light back to the pool for reuse.
   */
  destroyLight(lightId: string, activeChannelLights?: string[]): void {
    // Release handle back to pool (preferred)
    const handle = this.channelLightHandles.get(lightId);
    if (handle) {
      handle.release();
      this.channelLightHandles.delete(lightId);
      this.channelLights.delete(lightId);
    } else {
      // Fallback for legacy direct lights (shouldn't happen)
      const light = this.channelLights.get(lightId);
      if (light) {
        light.dispose();
        this.channelLights.delete(lightId);
      }
    }

    // Remove from active tracking if provided
    if (activeChannelLights) {
      const idx = activeChannelLights.indexOf(lightId);
      if (idx >= 0) {
        activeChannelLights.splice(idx, 1);
      }
    }

    logger.combat.debug(`[CombatEffectsController] Released pooled light: ${lightId}`);
  }

  /**
   * Cleanup all lights in the given list.
   * Releases all lights back to the pool for reuse.
   */
  cleanupLights(activeChannelLights: string[]): void {
    for (const lightId of activeChannelLights) {
      // Release handle back to pool
      const handle = this.channelLightHandles.get(lightId);
      if (handle) {
        handle.release();
        this.channelLightHandles.delete(lightId);
        this.channelLights.delete(lightId);
      } else {
        // Fallback for legacy direct lights
        const light = this.channelLights.get(lightId);
        if (light) {
          light.dispose();
          this.channelLights.delete(lightId);
        }
      }
    }
    activeChannelLights.length = 0;
  }

  // ============================================================
  // Static FX
  // ============================================================

  /**
   * Spawn static FX meshes attached to bones.
   * Returns array of spawned meshes.
   */
  spawnStaticFx(
    attacker: Character,
    fxSpecs: ProjectileSpec[],
    embeddedEntities: ExtractionResult | undefined
  ): AbstractMesh[] {
    const characterAsset = (attacker as unknown as { characterAsset?: CharacterAsset }).characterAsset;
    const skeleton = characterAsset?.skeleton;
    const rootMesh = characterAsset?.rootMesh;
    const spawned: AbstractMesh[] = [];

    for (const fxSpec of fxSpecs) {
      try {
        const fxMesh = createProjectileMesh(
          this.scene,
          fxSpec,
          `staticFx_${fxSpec.assetId}_${Date.now()}`,
          embeddedEntities
        );
        if (fxMesh) {
          // Attach to bone if specified
          const boneName = fxSpec.muzzleBone ?? 'mixamorig:RightHand';
          const bone = skeleton?.bones.find((b: Bone) => b.name === boneName);
          if (bone && rootMesh) {
            fxMesh.attachToBone(bone, rootMesh);
            fxMesh.position.set(0, 0, 0);
          } else if (rootMesh) {
            // Parent to character mesh if bone not found
            fxMesh.parent = rootMesh;
          }
          fxMesh.setEnabled(true);
          fxMesh.isVisible = true;
          spawned.push(fxMesh);
          logger.combat.debug(`Spawned static FX '${fxSpec.assetId}' attached to bone '${boneName}'`);
        }
      } catch (error) {
        logger.combat.error(`Failed to spawn static FX '${fxSpec.assetId}':`, error);
      }
    }

    return spawned;
  }

  /**
   * Spawn static FX from a preset request (minimal spec).
   */
  spawnStaticFxFromRequest(
    attacker: Character,
    fxRequests: Array<{ id?: string; scale?: number; boneName?: string }>,
    embeddedEntities: ExtractionResult | undefined
  ): AbstractMesh[] {
    const characterAsset = (attacker as unknown as { characterAsset?: CharacterAsset }).characterAsset;
    const skeleton = characterAsset?.skeleton;
    const rootMesh = characterAsset?.rootMesh;
    const spawned: AbstractMesh[] = [];

    for (const fxRequest of fxRequests) {
      try {
        // Create a minimal ProjectileSpec for the static FX
        const fxSpec: ProjectileSpec = {
          assetId: fxRequest.id ?? `staticFx_${Date.now()}`,
          type: 'static',
          speed: 0,
          directionMode: 'bone',
          scale: fxRequest.scale ?? 1,
        };

        const fxMesh = createProjectileMesh(
          this.scene,
          fxSpec,
          `staticFx_${fxRequest.id ?? Date.now()}`,
          embeddedEntities
        );

        if (fxMesh) {
          const boneName = fxRequest.boneName ?? 'mixamorig:RightHand';

          // Try embedded asset first
          let attached = false;
          if (skeleton && rootMesh) {
            const bone = skeleton.bones.find((b: Bone) => b.name === boneName);
            if (bone) {
              fxMesh.attachToBone(bone, rootMesh);
              fxMesh.position.set(0, 0, 0);
              attached = true;
            }
          }

          if (!attached && rootMesh) {
            fxMesh.parent = rootMesh;
          }

          fxMesh.setEnabled(true);
          fxMesh.isVisible = true;

          // Store ID for later removal
          (fxMesh as unknown as { _staticFxId?: string })._staticFxId = fxRequest.id;

          spawned.push(fxMesh);
          logger.combat.debug(`[CombatEffectsController] Spawned static FX request '${fxRequest.id ?? 'unnamed'}'`);
        }
      } catch (error) {
        logger.combat.error(`[CombatEffectsController] Failed to spawn static FX request:`, error);
      }
    }

    return spawned;
  }

  /**
   * Despawn static FX by ID.
   */
  despawnStaticFxById(activeStaticFx: AbstractMesh[], fxId: string): boolean {
    const fxIndex = activeStaticFx.findIndex((m) => (m as unknown as { _staticFxId?: string })._staticFxId === fxId);
    if (fxIndex >= 0) {
      const fxMesh = activeStaticFx[fxIndex];
      if (fxMesh && !fxMesh.isDisposed()) {
        fxMesh.dispose();
      }
      activeStaticFx.splice(fxIndex, 1);
      logger.combat.debug(`[CombatEffectsController] Despawned static FX: ${fxId}`);
      return true;
    }
    return false;
  }

  /**
   * Update static FX positions to follow bones.
   */
  updateStaticFxPositions(attacker: Character, activeStaticFx: AbstractMesh[]): void {
    const characterAsset = (attacker as unknown as { characterAsset?: CharacterAsset }).characterAsset;
    const skeleton = characterAsset?.skeleton;
    const rootMesh = characterAsset?.rootMesh;

    if (!skeleton || !rootMesh) return;

    // FX meshes attached via attachToBone automatically update
    // This method is here for any manual position updates if needed
    void activeStaticFx; // Used for potential future manual updates
  }

  /**
   * Cleanup all static FX meshes.
   */
  cleanupStaticFx(activeStaticFx: AbstractMesh[]): void {
    for (const fxMesh of activeStaticFx) {
      if (fxMesh && !fxMesh.isDisposed()) {
        fxMesh.dispose();
        logger.combat.debug(`[CombatEffectsController] Cleaned up static FX: ${fxMesh.name}`);
      }
    }
    activeStaticFx.length = 0;
  }

  // ============================================================
  // Attached Meshes
  // ============================================================

  /**
   * Attached mesh specification from attack config.
   */
  async loadAttachedMeshes(
    attacker: Character,
    specs: Array<{
      assetId: string;
      assetPath?: string;
      attachBone: string;
      scale?: number;
      localTransform?: {
        position?: Vector3;
        rotation?: Vector3;
        scale?: Vector3;
      };
      animationName?: string;
    }>
  ): Promise<AttachedMeshInstance[]> {
    const characterAsset = (attacker as unknown as { characterAsset?: CharacterAsset }).characterAsset;
    const rootMesh: AbstractMesh | undefined = characterAsset?.rootMesh;
    const skeleton: Skeleton | null | undefined = characterAsset?.skeleton;

    const instances: AttachedMeshInstance[] = [];

    for (const spec of specs) {
      try {
        let mesh: AbstractMesh | null = null;
        let animationGroup: AnimationGroup | undefined;

        if (!spec.assetPath) {
          throw new Error(
            `CombatEffectsController: Attached mesh '${spec.assetId}' missing assetPath. ` +
              `All attached meshes must specify an asset.`
          );
        }

        const asset = await SceneLoader.ImportMeshAsync('', '', spec.assetPath, this.scene);
        mesh = asset.meshes?.[0] ?? null;
        if (!mesh) {
          throw new Error(
            `CombatEffectsController: Failed to load attached mesh '${spec.assetId}' from '${spec.assetPath}'. ` +
              `No meshes found in asset.`
          );
        }
        if (spec.animationName && asset.animationGroups) {
          animationGroup = asset.animationGroups.find((g) => g.name === spec.animationName);
        }

        const scale = spec.scale ?? 1.0;
        mesh.scaling.copyFromFloats(scale, scale, scale);

        if (spec.localTransform?.position) {
          mesh.position.copyFrom(spec.localTransform.position);
        } else {
          mesh.position.set(0, 0, 0);
        }
        if (spec.localTransform?.rotation) {
          mesh.rotation.copyFrom(spec.localTransform.rotation);
        }
        if (spec.localTransform?.scale) {
          mesh.scaling.copyFrom(spec.localTransform.scale);
        }

        let attached = false;
        if (skeleton && rootMesh) {
          const bone = skeleton.bones.find((b: Bone) => b.name === spec.attachBone);
          if (bone) {
            mesh.attachToBone(bone, rootMesh);
            attached = true;
          }
        }

        if (!attached && rootMesh) {
          mesh.parent = rootMesh;
        }

        if (animationGroup) {
          animationGroup.start(true);
        }

        instances.push({ mesh, boneName: spec.attachBone, spec: spec as AttachedMeshSpec, animationGroup });
      } catch (error) {
        logger.core.error(
          `CombatEffectsController: Failed to load attached mesh ${spec.assetId} for ${attacker.getId()}`,
          error
        );
      }
    }

    return instances;
  }

  /**
   * Cleanup attached meshes.
   */
  cleanupAttachedMeshes(instances: AttachedMeshInstance[]): void {
    for (const instance of instances) {
      const detach = (instance.mesh as unknown as { detachFromBone?: () => void })?.detachFromBone;
      if (typeof detach === 'function') {
        detach.call(instance.mesh);
      } else {
        instance.mesh.parent = null;
      }
      if (instance.animationGroup) {
        instance.animationGroup.stop();
      }
      instance.mesh.dispose();
    }
  }

  // ============================================================
  // Debug Info
  // ============================================================

  getDebugInfo(): {
    activeLights: number;
    lightIds: string[];
  } {
    return {
      activeLights: this.channelLights.size,
      lightIds: Array.from(this.channelLights.keys()),
    };
  }
}
